<template>
  <div
    :id="`tabpanel-` + currentFilter?.toLowerCase()"
    class="BookingRooms"
    role="tabpanel">
    <div class="BookingRooms-list" role="list">
      <div v-if="loading">
        <room-skeleton v-for="n in 3" :key="n" />
      </div>

      <div
        v-for="(room, index) in rooms"
        v-else
        :key="room.availabilityRoom?.code ?? index"
        class="BookingRooms-listItem"
        role="listitem">
        <ai-accommodation-details-card
          :accommodation="room.accommodationRoom"
          :availability-room="room.availabilityRoom as AvailabilityRoom"
          :number-of-nights="bookingStore.dates.nights"
          :number-of-guests="bookingStore.currentUnconfiguredGuestsCount"
          :user-currency="userCurrency"
          :hotel="hotel"
          :all-taxes-included="allTaxesIncluded"
          @click="(productCode: string) => $emit('set-room', productCode)"
          @see-details="
            (button?: HTMLButtonElement) => onSeeDetails(room, button)
          " />
      </div>
    </div>

    <booking-rooms-details
      v-model="detailsDrawerOpen"
      :room="seeRoomDetails"
      @popin-close="buttonToFocusOnClose?.focus()" />
  </div>
</template>

<script setup lang="ts">
import { useBookingStore } from '../../store';
import RoomSkeleton from '../Skeletons/RoomSkeleton.vue';

import BookingRoomsDetails from './BookingRoomsDetails.vue';

import type {
  AvailabilityRoom,
  AvailableRoom,
  Hotel,
} from '~~/domains/graphql';
import { useHotelQuery } from '~~/domains/graphql';

type Props = {
  currentFilter?: string;
  rooms?: AvailableRoom[];
  loading: boolean;
  userCurrency?: string;
};

type Emits = {
  (event: 'set-room', productCode: string): void;
};

withDefaults(defineProps<Props>(), {
  rooms: () => [],
  currentFilter: undefined,
  userCurrency: undefined,
});
defineEmits<Emits>();

const route = useRoute();
const bookingStore = useBookingStore();
const allTaxesIncluded = useAreAllTaxesIncluded();

const hotelId: any =
  bookingStore.hotelId ||
  (route.params.ridcode as string) ||
  route.query.ridcode ||
  '0';

const { result: hotelResult } = useHotelQuery(
  { hotelId },
  {
    initialFetchPolicy: 'cache-first',
  },
);
const hotel = computed(() => hotelResult.value?.hotel as Hotel | undefined);

const seeRoomDetails = ref<AvailableRoom>();
const detailsDrawerOpen = computed({
  get() {
    return Boolean(seeRoomDetails.value);
  },
  set(value) {
    if (!value) {
      seeRoomDetails.value = undefined;
    }
  },
});

const buttonToFocusOnClose = ref<HTMLButtonElement | undefined>();

const onSeeDetails = (room: AvailableRoom, button?: HTMLButtonElement) => {
  seeRoomDetails.value = room;
  buttonToFocusOnClose.value = button;
};
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';

.BookingRooms-listItem {
  margin-bottom: constants.$margin-02;
}
</style>
